import { useState, useRef, useEffect, useMemo } from "react";
import styled from "styled-components";

import { CheckIcon, CrossIcon } from '../generic/Validate'

//Import UI components to be used
import { Form } from '../form/FormComponents';
import SelectMenuItem from "../select/SelectMenuItem";

//setup for language State
import useTranslation from "../../hooks/useTranslation";

//Import KeyPress Utils:
import useKeyPress from "../../hooks/useKeyPress";

//Controlling the Outside Click
import useOutsideClick from "../../hooks/useOutsideClick";

//Define Styled Components
const SearchContainer = styled.div`
    width: 100%;
    height: 100%;
    display: flex;
    flex-direction: column;
    justify-content: center;

    @media (max-width: ${({ theme }) => theme.breakpoint.xs}) {
        display: none;
    }
`

const InputField = styled(Form.Control)`
    padding-left: 1rem;
`

const SearchResults = styled.div`
    position: absolute;
    top: 42px;
    width: 100%;
    /* Never narrower than a readable entry, even under a narrow grid column.
       A field at the right edge opens it leftwards (dropdownAlign: 'right'). */
    min-width: 16rem;
    ${({ $alignRight }) => $alignRight ? 'right: 0;' : ''}
    max-height: 250px;
    overflow-y: auto;
    box-shadow: 0 2px 5px rgb(0 0 0 / 28%);
    z-index:101;
    
    &.visible {
      visibility: visible;
    }
  
    &.invisible {
        visibility: hidden!important;
    }
`;

const ListGroup = styled.ul`
    --list-group-bg: ${({ theme }) => theme.dropdown.background};;
    --list-group-border-radius: 0.375rem;
    display: flex;
    flex-direction: column;
    background-color: var(--list-group-bg);
    padding-left: 0;
    margin-bottom: 0;
    border-radius: var(--list-group-border-radius);
    overflow-x: hidden;
`

const AdditionalInfo = styled.div`
    font-size: 0.8rem;
    color: ${({ theme }) => theme.font.color};
    margin-top: 0.25rem;
    padding-left: 0.5rem;
    display: block;
`

const SelectControl = ({
    data,
    onSelect,
    placeholder,
    valueName,
    valueId,
    entity,
    isRed,
    defaultValue,
    options = {},
    testId,
    additionalInfo = null,
    autoFocus = false,
    name,
    labelId,
    inputId,
    onAdvance
}) => {
    const [isVisible, setVisibility] = useState(false);
    const [search, setSearch] = useState("");
    // Whether `search` is something the user typed, rather than the current
    // value shown in the input. Only typed text filters the list: opening the
    // field shows every entry, not just the one already picked.
    const [typed, setTyped] = useState(false);
    const [cursor, setCursor] = useState(-1);

    const searchResultRef = useRef(null);
    const myRef = useRef(null);
    const { t } = useTranslation();

    //Monitoring pressing Enter
    const enterPressed = useKeyPress('Enter');

    //Hide & Show the Menu
    const showSuggestion = () => setVisibility(true);
    const hideSuggestion = () => setVisibility(false);

    //Filtering Suggestion on Search — declared before effects so it can be in their dep arrays
    const suggestions = useMemo(() => {
        if (!search || !typed) return data.list;
        if (!data.list) return [];

        return data.list.filter(item =>
            item.name?.toLowerCase().includes(search.toLowerCase()) ||
            item.subtitle?.toLowerCase().includes(search.toLowerCase()) ||
            item.id?.toLowerCase().includes(search.toLowerCase())
        );
    }, [data.list, search, typed]);

    /*
     * A list whose entries carry a `shortcut` letter is picked from the
     * keyboard: the letter alone selects its entry and moves on to the next
     * field, so a journal line's type is one keystroke. The letters come with
     * the entries, translated with their names, because a letter follows the
     * word it abbreviates.
     */
    const shortcuts = useMemo(() => {
        const map = new Map();
        (data.list || []).forEach(item => {
            if (item.shortcut && !item.header) map.set(item.shortcut.toLocaleUpperCase(), item);
        });
        return map;
    }, [data.list]);

    const focusNextField = (input) => {
        const fields = Array.from(document.querySelectorAll('input, select, textarea, button'))
            .filter(el => !el.disabled && el.type !== 'hidden' && el.offsetParent !== null);
        fields[fields.indexOf(input) + 1]?.focus();
    };

    const handleChange = e => {
        const value = e.target.value;
        // Only a letter typed: deleting "Salg" down to "S" is not asking for S.
        const inserted = (e.nativeEvent?.inputType || 'insertText').startsWith('insert');
        const picked = inserted && [...value].length === 1 ? shortcuts.get(value.toLocaleUpperCase()) : undefined;
        if (picked) {
            setSearch(picked.name);
            setTyped(false);
            hideSuggestion();
            onSelect(picked);
            // A host that moves focus itself (a grid cell, whose neighbour is
            // not an input) says where "next" is.
            if (onAdvance) onAdvance();
            else focusNextField(e.target);
            return;
        }
        setSearch(value);
        setTyped(true);
        showSuggestion();
    };

    //Handle Click Outside the Object
    const handleClickOutside = () => {
        hideSuggestion();
        setTyped(false);
        //if click outside then the search = text in the input, is set back to its original value
        if (valueName !== '' && search === '') {
            setSearch(valueName)
        }
    };
    useOutsideClick(myRef, handleClickOutside, 'modal');

    // The input's own Enter handler picks first and closes the list; a closed
    // list is a choice already made, not one to make again.
    useEffect(() => {
        if (enterPressed && isVisible && cursor >= 0 && suggestions[cursor] && !suggestions[cursor].header) {
            setSearch(suggestions[cursor].name);
            setTyped(false);
            hideSuggestion();
            onSelect(suggestions[cursor]);
        }
    }, [enterPressed, suggestions]);

    //Load the value that is already in the DB
    useEffect(() => {
        if (valueName === '' && defaultValue && defaultValue !== '') {
            setSearch(defaultValue.id)
        } else {
            setSearch(valueName)
        }
        setTyped(false)
    }, [valueName])

    // Scroll the highlighted item into view when cursor changes
    useEffect(() => {
        if (cursor < 0 || cursor > suggestions.length || !searchResultRef.current) return;
        const listItems = Array.from(searchResultRef.current.children);
        listItems[cursor]?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }, [cursor, suggestions]);

    // Reset cursor and scroll to top when search changes. Typing highlights the
    // first match, so Enter takes what was typed for.
    useEffect(() => {
        setCursor(typed && search ? suggestions.findIndex(item => !item.header) : -1);
        searchResultRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, [search, typed]);

    //Keyboard events
    const keyboardNavigation = e => {
        if (e.ctrlKey) return

        if (e.key === "ArrowDown") {
            isVisible
                ? setCursor(c => (c < suggestions.length - 1 ? c + 1 : c))
                : showSuggestion();
        }

        if (e.key === "ArrowUp") {
            setCursor(c => (c > 0 ? c - 1 : 0));
        }

        if (e.key === "Escape") {
            hideSuggestion();
        }

        // Leaving with Tab while searching takes the highlighted entry, or the
        // only one left — what was typed is what was meant.
        if (e.key === "Tab" && isVisible && (typed || cursor >= 0)) {
            const item = cursor >= 0 ? suggestions[cursor] : (suggestions.length === 1 ? suggestions[0] : null);
            if (item && !item.header) pickFromKeyboard(e, item);
        }

        if (e.key === "Enter" && isVisible && cursor >= 0) {
            e.preventDefault()
            const item = suggestions[cursor];
            if (item && !item.header) pickFromKeyboard(e, item);
        }
    };

    /*
     * A pick made with Enter or Tab is the key's whole meaning: it stops here,
     * so a host listening further up (a grid cell, which clears a picker left
     * empty) does not act on it as well. A host that moves focus itself says
     * where "next" is with onAdvance; elsewhere Tab keeps its native move.
     */
    const pickFromKeyboard = (e, item) => {
        e.stopPropagation();
        //Checks if displayId is true. If it is uses the ID instead of the name to fill in the input field
        setSearch(options.displayId ? item.id : item.name);
        setTyped(false);
        hideSuggestion();
        onSelect(item);
        if (onAdvance) {
            e.preventDefault();
            onAdvance();
        }
    };

    const handleSelect = (e, item) => {
        e.preventDefault()
        //Checks if displayId is true. If it is uses the ID instead of the name to fill in the input field
        if (options.displayId) { setSearch(item.id) }
        else { setSearch(item.name) };
        setTyped(false);
        hideSuggestion();
        onSelect(item);
    }

    return (

            <SearchContainer ref={myRef}>
                <Form.Group $margin='0' style={{ position: 'relative', height: '100%' }}>
                    <InputField
                        type="text"
                        id={inputId || name || entity}
                        name={name || entity}
                        autoComplete="off"
                        value={search || ''}
                        onClick={showSuggestion}
                        onChange={handleChange}
                        onFocus={e => {
                            // Selected, so the letter typed replaces the value
                            // rather than being appended to it.
                            if (shortcuts.size > 0) e.target.select();
                            showSuggestion();
                        }}
                        onBlur={(e) => {
                            if (!myRef.current?.contains(e.relatedTarget)) hideSuggestion()
                        }}
                        onKeyDown={e => keyboardNavigation(e)}
                        placeholder={t(placeholder)}
                        data-testid={`${testId}`}
                        aria-label={t(placeholder)}
                        aria-labelledby={labelId || undefined}
                        aria-autocomplete='list'
                        aria-controls='select-menu'
                        autoFocus={autoFocus}
                        {...(isRed && { required: true })}
                    />
                    {valueName && <CheckIcon></CheckIcon>}
                    {!valueName && !isRed && <CrossIcon></CrossIcon>}
                </Form.Group>
                {/*                <AdditionalInfo $show={showAdditionalInfo}>
                    {getExchangeRateMessage()}
                    {exchangeDate && ` (${exchangeDate})`}
                </AdditionalInfo> */}
                {additionalInfo && <AdditionalInfo>
                    {additionalInfo}
                </AdditionalInfo>
                }
                <SearchResults id={`select-search-result-${entity}`}
                    $alignRight={options.dropdownAlign === 'right'}
                    className={`${isVisible ? "visible" : "invisible"}`}
                >
                    <ListGroup ref={searchResultRef} id='select-menu' role='listbox'>
                        {suggestions && suggestions.map((item, idx) => (
                            <SelectMenuItem
                                displayId={options.displayId || false}
                                key={item.id}
                                onSelectItem={e => handleSelect(e, item)}
                                isHighlighted={cursor === idx ? true : false}
                                {...item}
                                // showSubtitle: false — one line per entry, where a
                                // second line makes the list hard to scan.
                                subtitle={options.showSubtitle === false ? undefined : item.subtitle}
                                idx={idx}
                                testId={testId}
                            />
                        ))}
                    </ListGroup>
                </SearchResults>
            </SearchContainer>
    )
}

export default SelectControl;
